import * as React from 'react';
import { Box, Button } from '@mui/material';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CircularProgress from '@mui/material/CircularProgress';
import {
  updateLastActive,
  listValidatedEmail,
  listValidatedEmailbySingleUser,
} from '../../utils/templatesApi';
import { toast } from 'react-toastify';
import { EmailValidationTable } from './EmailValidationTable';
import { useState } from 'react';
import DetailedTable from './DetailedTable';

dayjs.extend(relativeTime);

export default function ValidatedEmailListing({ render, role, userId }) {
  const [usersId, setUserId] = React.useState(null);
  const [groupedUsers, setGroupedUsers] = useState([]);
  const [userDetails, setUserDetails] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState([null, null]);
  const [search, setSearch] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(0);

  React.useEffect(() => {
    getUsersList();
  }, [render]);

  React.useEffect(() => {
    if (role === 'user') {
      getSingleUserValidatedEmails(userId);
    }
  }, [page, rowsPerPage, render]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  React.useEffect(() => {
    const [start, end] = value;
    if (usersId !== null && ((start && end) || (!start && !end))) {
      const timer = setTimeout(() => {
        getSingleUserValidatedEmails(usersId);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [search, value, userId]);

  React.useEffect(() => {
    if (usersId !== null) {
      getSingleUserValidatedEmails(usersId);
    }
  }, [page, rowsPerPage]);

  const getUsersList = async () => {
    try {
      setLoading(true);
      const resp = await listValidatedEmail();
      setGroupedUsers(resp?.data);
      setLoading(false);
    } catch (error) {
      toast.error('Error in fetching users list');
      setLoading(false);
    }
  };

  const getSingleUserValidatedEmails = async (id) => {
    try {
      setLoading(true);
      const filters = {
        search,
        startDate: value?.[0] ? dayjs(value[0]).toISOString() : undefined,
        endDate: value?.[1] ? dayjs(value[1]).toISOString() : undefined,
        page: page + 1,
        limit: rowsPerPage,
      };
      const resp = await listValidatedEmailbySingleUser(id, filters);
      setUserDetails(resp?.data);
      setTotalCount(resp?.meta?.totalItems || 0);
      setLoading(false);
    } catch (error) {
      toast.error('Error in fetching user details');
      setLoading(false);
    }
  };

  const handleView = async (id) => {
    setShowDetails(true);
    setUserId(id);
    updateLastActive(userId, new Date());
    getSingleUserValidatedEmails(id);
  };
  const handleBack = () => {
    setShowDetails(false);
    setUserId(null);
    updateLastActive(userId, new Date());
    setPage(0);
    setValue([null, null]);
    setSearch('');
    setRowsPerPage(50);
  };

  function Row(props) {
    const { user } = props;
    return (
      <React.Fragment>
        <TableRow>
          <TableCell></TableCell>
          <TableCell component="th" scope="row">
            {user?.user.first_name} {user?.user.last_name}
          </TableCell>
          <TableCell component="th" scope="row">
            {user?.totalValidations}
          </TableCell>
          <TableCell component="th" scope="row">
            <Button
              variant="contained"
              onClick={() => handleView(user.userId)}
              sx={{
                textTransform: 'capitalize',
                backgroundColor: '#111827',
                width: '80px',
              }}
            >
              view
            </Button>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '75vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : ['admin', 'super-admin'].includes(role) ? (
        showDetails ? (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '10px',
              }}
            >
              <Button
                variant="contained"
                sx={{
                  textTransform: 'capitalize',
                  width: '100px',
                  backgroundColor: '#49d68a5e',
                  color: '#111827',
                }}
                onClick={handleBack}
              >
                Back
              </Button>
            </Box>{' '}
            <DetailedTable
              setShowDetails={setShowDetails}
              loading={loading}
              userList={userDetails}
              usersId={usersId}
              setSearch={setSearch}
              value={value}
              setValue={setValue}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              totalCount={totalCount}
              setTotalCount={setTotalCount}
              page={page}
              setPage={setPage}
              search={search}
              handleSearchChange={handleSearchChange}
            />
          </>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ minWidth: '80vw', maxHeight: '75vh', overflow: 'auto' }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 40 }}
                >
                  <TableCell />
                  <TableCell sx={{ fontWeight: 'bold' }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', alignContent: 'right' }}>
                    Total Validated Emails
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', alignContent: 'right' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedUsers?.map((user, index) => (
                  <Row key={index} user={user} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <EmailValidationTable
          loading={loading}
          userList={userDetails}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalCount={totalCount}
          setTotalCount={setTotalCount}
          page={page}
          setPage={setPage}
        />
      )}
    </>
  );
}
